import { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from '../../ui/button';
import { Textarea } from '../../ui/textarea';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Plus, Edit2, X, Check, Save, Trash2, Info, Sparkles, Loader2, Eye, FileText, Search } from 'lucide-react';
import { Badge } from '../../ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../ui/tooltip';
import type { WatchTopic } from '../../../types';
import { DeleteConfirmModal } from '../../common/DeleteConfirmModal';
import { TabHeader } from '../../common/TabHeader';
import { useLanguage } from '../../../contexts/LanguageContext';
import { AnimatedIconButton } from '../../common/AnimatedIconButton';
import { ActionButtonBase } from '../../common/ActionButtonBase';
import { watchTopicsService } from '../../../services/watchTopicsService';
import { FeedItemsModal } from '../sources/FeedItemsModal';
import { SuggestTopicsModal } from '../watch/SuggestTopicsModal';
import { EmptyState } from '../../common/EmptyState';
import { Logger } from '../../../utils/logger';
import { useReadOnly } from '../../../hooks/useReadOnly';
import { useAuthContext } from '../../../contexts/AuthContext';

interface WatchPanelProps {
  watchTopics: WatchTopic[];
  /** V4-WATCH-KEYWORDS-LOADING — ids des topics dont les mots-clés se génèrent en
   *  arrière-plan (affiche un indicateur "génération en cours" sur la card). */
  enrichingTopicIds?: Set<string>;
  onAddTopic: (topic: Omit<WatchTopic, 'id'>) => void;
  onUpdateTopic: (id: string, topic: Omit<WatchTopic, 'id'>) => void;
  onDeleteTopic: (id: string) => void;
  onClose: () => void;
  /** Backlog UX 19-05 — passé par la Sidebar (qui hold l'état onboarding global).
   *  Détermine si "Suggérer via IA" est cliquable (requiert positioning défini). */
  positioningReady?: boolean;
}

export function WatchTab({ watchTopics, enrichingTopicIds, onAddTopic, onUpdateTopic, onDeleteTopic, onClose, positioningReady = true }: WatchPanelProps) {
  const { t } = useLanguage();
  const readOnly = useReadOnly();
  const { currentSpace } = useAuthContext();
  // Backlog UX 19-05 — bloque "Suggérer via IA" tant que le positionnement
  // n'est pas généré (la suggestion s'appuie dessus). Source de vérité :
  // prop `positioningReady` calculée dans Sidebar via useOnboardingProgress
  // (qui a accès à TOUTES les données nécessaires : systemPrompt, isTemplate…).
  // TODO V3-2.1 — Conditionner également "Nouveau centre d'intérêt" (création
  // manuelle) à `positioningReady` ? Aujourd'hui seul "Suggérer via IA" est
  // grisé. La création manuelle reste accessible. À arbitrer côté produit :
  // forcer l'ordre wizard (positioning → topics) ou laisser la flexibilité
  // d'ajouter un topic puis revenir au positionnement. Décision en attente.
  const aiSuggestDisabled = !currentSpace?.id || !positioningReady;
  const aiSuggestTooltip = !positioningReady
    ? t.onboarding?.lockedTopicsTooltip ?? "Définissez d'abord votre positionnement pour débloquer la suggestion IA"
    : undefined;
  const [isCreating, setIsCreating] = useState(false);
  const [showSuggestModal, setShowSuggestModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deletingTopic, setDeletingTopic] = useState<WatchTopic | null>(null);
  const [addingKeyword, setAddingKeyword] = useState<string | null>(null);
  const [newKeywordInput, setNewKeywordInput] = useState('');
  const [isGeneratingKeywords, setIsGeneratingKeywords] = useState(false);

  // V4-WATCH-ADD-SCROLLTOP 2026-06-22 — au clic "+", le formulaire de création est
  // rendu en haut de la liste ; on remonte le scroll pour que l'utilisateur le voie
  // (sinon il reste hors écran s'il avait scrollé bas).
  const scrollRef = useRef<HTMLDivElement>(null);

  // Feed items modal state
  const [viewingTopic, setViewingTopic] = useState<WatchTopic | null>(null);
  
  // Item counts per topic (loaded asynchronously)
  const [topicItemCounts, setTopicItemCounts] = useState<Map<string, number>>(new Map());

  // Load item counts when topics change
  useEffect(() => {
    if (!watchTopics || watchTopics.length === 0) return;
    let cancelled = false;
    const loadCounts = async () => {
      for (const topic of watchTopics) {
        if (cancelled) break;
        try {
          const count = await watchTopicsService.getItemsCount(topic.id);
          if (!cancelled) {
            setTopicItemCounts(prev => {
              const next = new Map(prev);
              next.set(topic.id, count);
              return next;
            });
          }
        } catch (_) {
          // Silently ignore — topic may not have items yet
        }
      }
    };
    loadCounts();
    return () => { cancelled = true; };
  }, [watchTopics]);

  // Form state for creation
  const [newPrompt, setNewPrompt] = useState('');
  const [newKeywords, setNewKeywords] = useState<string[]>([]);

  // Form state for editing
  const [editPrompt, setEditPrompt] = useState('');
  const [editKeywords, setEditKeywords] = useState<string[]>([]);
  const [editingKeywordIndex, setEditingKeywordIndex] = useState<number | null>(null);
  const [editingKeywordValue, setEditingKeywordValue] = useState('');

  const handleStartCreate = () => {
    setIsCreating(true);
    setNewPrompt('');
    setNewKeywords([]);
    // Laisser le form se monter avant de scroller en haut (V4-WATCH-ADD-SCROLLTOP).
    setTimeout(() => scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' }), 0);
  };

  const handleCancelCreate = () => {
    setIsCreating(false);
  };

  const handleSaveCreate = () => {
    if (newPrompt.trim()) {
      // Enregistrement instantané. Si aucun mot-clé n'a été ajouté manuellement,
      // la génération IA se fait en arrière-plan dans le hook (useWatchTopics →
      // enrichKeywords) pour ne pas bloquer le save de ~1–3s (appel LLM inline).
      onAddTopic({
        prompt: newPrompt.trim(),
        keywords: newKeywords,
        enabled: true,
        createdDate: new Date()
      });
      setIsCreating(false);
    }
  };

  const handleGenerateKeywords = async (isEditing: boolean) => {
    const prompt = isEditing ? editPrompt : newPrompt;
    if (!prompt.trim()) return;

    setIsGeneratingKeywords(true);
    try {
      const response = await watchTopicsService.generateKeywords(prompt.trim());
      const generatedKeywords = response.keywords || [];
      
      if (isEditing) {
        // Merge with existing keywords (avoid duplicates)
        const merged = [...new Set([...generatedKeywords, ...editKeywords])];
        setEditKeywords(merged);
      } else {
        const merged = [...new Set([...generatedKeywords, ...newKeywords])];
        setNewKeywords(merged);
      }
    } catch (error) {
      Logger.error('Error generating keywords:', error);
    } finally {
      setIsGeneratingKeywords(false);
    }
  };

  const handleStartEdit = (topic: WatchTopic) => {
    setEditingId(topic.id);
    setEditPrompt(topic.prompt);
    setEditKeywords(topic.keywords);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
  };

  const handleSaveEdit = () => {
    if (!editingId) return;

    if (editPrompt.trim()) {
      onUpdateTopic(editingId, {
        prompt: editPrompt.trim(),
        keywords: editKeywords,
        enabled: watchTopics.find(t => t.id === editingId)?.enabled ?? true,
        createdDate: watchTopics.find(t => t.id === editingId)?.createdDate
      });
      setEditingId(null);
    }
  };

  const handleDeleteClick = (topic: WatchTopic) => {
    setDeletingTopic(topic);
  };

  const handleConfirmDelete = () => {
    if (deletingTopic) {
      onDeleteTopic(deletingTopic.id);
      setDeletingTopic(null);
    }
  };

  const handleAddKeyword = (keyword: string, isEditing: boolean) => {
    if (isEditing) {
      const currentKeywords = editKeywords;
      if (!currentKeywords.includes(keyword.trim())) {
        const updated = [keyword.trim(), ...currentKeywords].filter(k => k.length > 0);
        setEditKeywords(updated);
      }
    } else {
      const currentKeywords = newKeywords;
      if (!currentKeywords.includes(keyword.trim())) {
        const updated = [keyword.trim(), ...currentKeywords].filter(k => k.length > 0);
        setNewKeywords(updated);
      }
    }
  };

  const handleRemoveKeyword = (keyword: string, isEditing: boolean) => {
    if (isEditing) {
      const currentKeywords = editKeywords;
      const filtered = currentKeywords.filter(k => k !== keyword);
      setEditKeywords(filtered);
    } else {
      const currentKeywords = newKeywords;
      const filtered = currentKeywords.filter(k => k !== keyword);
      setNewKeywords(filtered);
    }
  };

  const handleAddKeywordInput = (isEditing: boolean) => {
    if (newKeywordInput.trim()) {
      handleAddKeyword(newKeywordInput.trim(), isEditing);
      setNewKeywordInput('');
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header — coque partagée V4-FRONT-HEADERS-UNIFORM */}
      <TabHeader
        title={t.watch.title}
        onClose={onClose}
        actions={
          !readOnly && !isCreating && (
            <>
              {/* Backlog V3-2.2 — bouton IA + tooltip Radix (au lieu de title=
                  natif HTML qui ne s'affichait pas sur button[disabled]). */}
              {aiSuggestDisabled && aiSuggestTooltip ? (
                <TooltipProvider delayDuration={150}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="inline-flex">
                        <ActionButtonBase
                          icon={Sparkles}
                          label={t.watch.suggestButton ?? 'Générer des mots-clés'}
                          onClick={() => setShowSuggestModal(true)}
                          variant="default"
                          expanded
                          disabled
                        />
                      </span>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="max-w-xs text-center">{aiSuggestTooltip}</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ) : (
                <ActionButtonBase
                  icon={Sparkles}
                  label={t.watch.suggestButton ?? 'Générer des mots-clés'}
                  onClick={() => setShowSuggestModal(true)}
                  variant="default"
                  expanded
                  disabled={aiSuggestDisabled}
                />
              )}
              {/* 1.3 — l'ajout manuel devient secondaire (variant neutral,
                  label au survol) pour laisser la suggestion IA en CTA primaire. */}
              <ActionButtonBase
                icon={Plus}
                label={t.watch.add}
                onClick={handleStartCreate}
                variant="neutral"
                revealLabelOnHover={true}
              />
            </>
          )
        }
      />

      {/* Content */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-6">
        {/* Create form */}
        {!readOnly && isCreating && (
          <div className="rounded-lg p-4 mb-3" style={{ backgroundColor: 'var(--color-surface)', border: '1px solid var(--color-border)' }}>
            <div className="space-y-4">
              {/* Description (anciennement prompt) */}
              <div>
                <div className="flex items-center gap-1.5 mb-2">
                  <Label htmlFor="new-prompt" className="text-xs">{t.watch.description || t.watch.prompt}</Label>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="w-3.5 h-3.5 cursor-help" style={{ color: 'var(--color-text-secondary)' }} />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="text-sm max-w-xs">{t.watch.descriptionTooltip}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <Textarea
                  id="new-prompt"
                  value={newPrompt}
                  onChange={(e) => setNewPrompt(e.target.value)}
                  placeholder={t.watch.descriptionPlaceholder || t.watch.promptPlaceholder}
                  className="rounded-lg resize-none h-20"
                  maxLength={50}
                />
                <div className="flex justify-end mt-1">
                  <span className={`text-xs ${newPrompt.length > 45 ? 'text-red-400' : newPrompt.length > 40 ? 'text-orange-400' : ''}`}
                    style={newPrompt.length <= 40 ? { color: 'var(--color-text-secondary)' } : {}}>
                    {newPrompt.length}/50
                  </span>
                </div>
                {(newPrompt.includes(',') || newPrompt.includes('.')) && (
                  <p className="text-xs mt-1" style={{ color: 'var(--color-warning)' }}>
                    {t.watch.keywordWarningChars || 'Évitez les virgules et points — utilisez des mots simples'}
                  </p>
                )}
              </div>

              <div className="flex gap-2 justify-end pt-3">
                <AnimatedIconButton
                  icon={X}
                  label={t.common.cancel}
                  onClick={handleCancelCreate}
                  variant="secondary"
                />
                <AnimatedIconButton
                  icon={Save}
                  label={t.common.save}
                  onClick={handleSaveCreate}
                  variant="success"
                  disabled={!newPrompt.trim()}
                />
              </div>
            </div>
          </div>
        )}

        {(!watchTopics || watchTopics.length === 0) && !isCreating ? (
          // UX backlog 11.3 : empty state engageant avec icône Eye + double CTA.
          <EmptyState
            icon={Eye}
            title={t.watch.emptyTitle ?? 'Aucun centre d\'intérêt'}
            description={t.watch.emptyDescription ?? t.watch.none}
            primaryCta={!readOnly ? {
              icon: Sparkles,
              label: t.watch.suggestButton ?? 'Générer des mots-clés',
              onClick: () => setShowSuggestModal(true),
              disabled: aiSuggestDisabled,
              disabledTooltip: aiSuggestTooltip,
            } : undefined}
            secondaryCta={!readOnly ? {
              icon: Plus,
              label: t.watch.add,
              onClick: handleStartCreate,
            } : undefined}
          />
        ) : watchTopics && watchTopics.length > 0 ? (
          <div className="space-y-3">{watchTopics.map((topic) => {
            const isEditing = editingId === topic.id;

            return (
              <div
                key={topic.id}
                className="rounded-lg border transition-all"
                style={{
                  backgroundColor: 'var(--color-surface)',
                  borderColor: 'var(--color-border)'
                }}
                onMouseEnter={(e) => {
                  if (!isEditing) {
                    e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isEditing) {
                    e.currentTarget.style.boxShadow = 'none';
                  }
                }}
              >
                {isEditing ? (
                  // Edit mode
                  <div className="p-4 space-y-3">
                    {/* Description (anciennement prompt) */}
                    <div>
                      <div className="flex items-center gap-1.5 mb-2">
                        <Label htmlFor={`edit-prompt-${topic.id}`} className="text-xs">{t.watch.description || t.watch.prompt}</Label>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Info className="w-3.5 h-3.5 cursor-help" style={{ color: 'var(--color-text-secondary)' }} />
                            </TooltipTrigger>
                            <TooltipContent>
                              <p className="text-sm max-w-xs">{t.watch.descriptionTooltip}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                      <Textarea
                        id={`edit-prompt-${topic.id}`}
                        value={editPrompt}
                        onChange={(e) => setEditPrompt(e.target.value)}
                        placeholder={t.watch.descriptionPlaceholder || t.watch.promptPlaceholder}
                        className="rounded-lg resize-none h-20"
                        maxLength={350}
                      />
                      <div className="flex justify-end mt-1">
                        <span className={`text-xs ${editPrompt.length > 340 ? 'text-red-400' : editPrompt.length > 300 ? 'text-orange-400' : ''}`}
                          style={editPrompt.length <= 300 ? { color: 'var(--color-text-secondary)' } : {}}>
                          {editPrompt.length}/350
                        </span>
                      </div>
                    </div>

                    {/* Mots-clés */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-1.5">
                          <Label className="text-xs">{t.watch.keywords}</Label>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Info className="w-3.5 h-3.5 cursor-help" style={{ color: 'var(--color-text-secondary)' }} />
                              </TooltipTrigger>
                              <TooltipContent>
                                <p className="text-sm max-w-xs">{t.watch.keywordsTooltip}</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                        <ActionButtonBase
                          icon={Sparkles}
                          label={isGeneratingKeywords ? t.watch.generatingKeywords : (t.watch.generateViaAI || 'Générer des mots-clés')}
                          onClick={() => handleGenerateKeywords(true)}
                          disabled={isGeneratingKeywords || !editPrompt.trim()}
                          loading={isGeneratingKeywords}
                          variant="default"
                          size="default"
                          revealLabelOnHover={false}
                          expanded={true}
                        />
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {addingKeyword === 'edit' ? (
                          <div className="flex items-center gap-1 pl-3 pr-1 h-7 rounded-full border-2" style={{ backgroundColor: 'var(--color-surface)', borderColor: 'var(--color-primary)' }}>
                            <input
                              type="text"
                              value={newKeywordInput}
                              onChange={(e) => setNewKeywordInput(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && newKeywordInput.trim()) {
                                  e.preventDefault();
                                  handleAddKeywordInput(true);
                                  setAddingKeyword(null);
                                } else if (e.key === 'Escape') {
                                  setNewKeywordInput('');
                                  setAddingKeyword(null);
                                }
                              }}
                              placeholder={t.watch.newKeyword}
                              className="bg-transparent border-0 outline-none text-sm w-32"
                              autoFocus
                            />
                            <button
                              onClick={() => {
                                handleAddKeywordInput(true);
                                setAddingKeyword(null);
                              }}
                              className="p-0.5"
                              style={{ color: 'var(--color-success)' }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.opacity = '0.8';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.opacity = '1';
                              }}
                            >
                              <Check className="w-3 h-3" />
                            </button>
                            <button
                              onClick={() => {
                                setNewKeywordInput('');
                                setAddingKeyword(null);
                              }}
                              className="p-0.5"
                              style={{ color: 'var(--color-danger)' }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.opacity = '0.8';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.opacity = '1';
                              }}
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setAddingKeyword('edit')}
                            className="flex items-center justify-center h-7 w-7 rounded-full border-2 transition-colors flex-shrink-0"
                            style={{ borderColor: 'var(--color-border)' }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.borderColor = 'var(--color-text-secondary)';
                              e.currentTarget.style.backgroundColor = 'var(--color-surface-hover)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.borderColor = 'var(--color-border)';
                              e.currentTarget.style.backgroundColor = 'transparent';
                            }}
                          >
                            <Plus className="w-4 h-4" style={{ color: 'var(--color-text-primary)' }} />
                          </button>
                        )}
                        {editKeywords.map((keyword, idx) => (
                          editingKeywordIndex === idx ? (
                            <div key={idx} className="flex items-center gap-1 pl-3 pr-1 h-7 rounded-full border-2" style={{ borderColor: 'var(--color-primary)', backgroundColor: 'var(--color-surface)' }}>
                              <input
                                type="text"
                                value={editingKeywordValue}
                                onChange={(e) => setEditingKeywordValue(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' && editingKeywordValue.trim()) {
                                    const updated = [...editKeywords];
                                    updated[idx] = editingKeywordValue.trim();
                                    setEditKeywords(updated);
                                    setEditingKeywordIndex(null);
                                  } else if (e.key === 'Escape') {
                                    setEditingKeywordIndex(null);
                                  }
                                }}
                                className="bg-transparent border-0 outline-none text-xs w-24"
                                autoFocus
                              />
                              <button
                                className="p-0.5"
                                style={{ color: 'var(--color-success)' }}
                                onClick={() => {
                                  if (editingKeywordValue.trim()) {
                                    const updated = [...editKeywords];
                                    updated[idx] = editingKeywordValue.trim();
                                    setEditKeywords(updated);
                                  }
                                  setEditingKeywordIndex(null);
                                }}
                              >
                                <Check className="w-3 h-3" />
                              </button>
                              <button className="p-0.5" style={{ color: 'var(--color-danger)' }} onClick={() => setEditingKeywordIndex(null)}>
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          ) : (
                            <div
                              key={idx}
                              className="flex items-center gap-1.5 px-3 h-7 rounded-full hover:opacity-80 transition-colors"
                              style={{ backgroundColor: 'var(--color-accent)', color: 'var(--color-text-light)' }}
                            >
                              <span
                                className="text-xs cursor-pointer"
                                onClick={() => { setEditingKeywordIndex(idx); setEditingKeywordValue(keyword); }}
                              >
                                {keyword}
                              </span>
                              <button onClick={(e) => { e.stopPropagation(); handleRemoveKeyword(keyword, true); }}>
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          )
                        ))}
                      </div>
                    </div>

                    <div className="flex gap-2 justify-end pt-3">
                      <AnimatedIconButton
                        icon={X}
                        label={t.common.cancel}
                        onClick={handleCancelEdit}
                        variant="secondary"
                      />
                      <AnimatedIconButton
                        icon={Save}
                        label={t.common.save}
                        onClick={handleSaveEdit}
                        variant="success"
                        disabled={!editPrompt.trim()}
                      />
                    </div>
                  </div>
                ) : (
                  // View mode
                  <div className="p-4">
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <p style={{ color: 'var(--color-text-primary)' }}>{topic.prompt}</p>
                          {/* Item count chip */}
                          {(() => {
                            const count = topicItemCounts.get(topic.id);
                            if (count === undefined || count === 0) return null;
                            return (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Badge 
                                      className="text-xs rounded-full flex items-center gap-1 cursor-help"
                                      style={{ 
                                        backgroundColor: 'var(--color-primary-light)', 
                                        color: 'var(--color-primary)' 
                                      }}
                                    >
                                      <Search className="w-3 h-3" />
                                      {count}
                                    </Badge>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p className="text-xs max-w-xs">{(t.watch?.webSearchItemsTooltip || '{count} résultat(s) web collecté(s) lors des générations').replace('{count}', String(count))}</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            );
                          })()}
                        </div>
                      </div>
                      <div className="flex gap-1 flex-shrink-0">
                        {topicItemCounts.get(topic.id) !== undefined && topicItemCounts.get(topic.id)! > 0 && (
                          <ActionButtonBase
                            onClick={() => setViewingTopic(topic)}
                            icon={Eye}
                            label={t.watch?.viewItems || 'Consulter'}
                            variant="default"
                            appearance="outline"
                            revealLabelOnHover={true}
                            ariaLabel={t.watch?.viewItems || 'Consulter les résultats web'}
                            title={t.watch?.viewItems || 'Consulter les résultats web'}
                          />
                        )}
                        {!readOnly && (
                          <ActionButtonBase
                            onClick={() => handleStartEdit(topic)}
                            icon={Edit2}
                            label={t.common.edit || 'Modifier'}
                            variant="neutral"
                            appearance="outline"
                            revealLabelOnHover={true}
                            ariaLabel={t.common.edit || 'Modifier'}
                            title={t.common.edit || 'Modifier'}
                          />
                        )}
                        {!readOnly && (
                          <ActionButtonBase
                            onClick={() => handleDeleteClick(topic)}
                            icon={Trash2}
                            label={t.common.delete || 'Supprimer'}
                            variant="danger"
                            appearance="outline"
                            revealLabelOnHover={true}
                            ariaLabel={t.common.delete || 'Supprimer'}
                            title={t.common.delete || 'Supprimer'}
                          />
                        )}
                      </div>
                    </div>

                    {topic.keywords.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {topic.keywords.map((keyword, idx) => (
                          <span
                            key={idx}
                            className="text-xs px-2 py-0.5 rounded-full"
                            style={{ backgroundColor: 'var(--color-surface-hover)', color: 'var(--color-text-secondary)' }}
                          >
                            {keyword}
                          </span>
                        ))}
                      </div>
                    ) : enrichingTopicIds?.has(topic.id) ? (
                      // V4-WATCH-KEYWORDS-LOADING — feedback pendant la génération IA en arrière-plan.
                      <div className="flex items-center gap-2 text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                        <Loader2 className="w-3.5 h-3.5 animate-spin" style={{ color: 'var(--color-primary)' }} />
                        <span>{t.watch?.keywordsGenerating || 'Génération des mots-clés en cours…'}</span>
                      </div>
                    ) : null}
                  </div>
                )}
              </div>
            );
          })}</div>
        ) : null}
      </div>

      {/* Delete confirmation modal */}
      <DeleteConfirmModal
        open={!!deletingTopic}
        onClose={() => setDeletingTopic(null)}
        onConfirm={handleConfirmDelete}
        title={t.watch.deleteTitle}
        description={t.watch.deleteDescription}
      />

      {/* Feed items detail modal for watch topic */}
      {viewingTopic && (
        <FeedItemsModal
          open={!!viewingTopic}
          onClose={() => setViewingTopic(null)}
          sourceId={viewingTopic.id}
          title={viewingTopic.prompt}
          mode="topic"
        />
      )}

      {/* AI suggestion modal — propose 3-5 topics depuis le positionnement */}
      <SuggestTopicsModal
        open={showSuggestModal}
        spaceId={currentSpace?.id}
        onClose={() => setShowSuggestModal(false)}
        onAddTopic={onAddTopic}
      />
    </div>
  );
}